import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonDialoguesService } from '@flyfreely-portal-ui/common-dialogues';
import {
    FlyFreelyLoggingService,
    LocationDetailsDto,
    LocationFeatureDto,
    NameValue
} from '@flyfreely-portal-ui/flyfreely';
import { MODAL_OPTIONS } from '@flyfreely-portal-ui/ngx-bootstrap-customisation';
import { Angulartics2 } from 'angulartics2';
import { BsModalRef, BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import {
    bufferAreaFeature,
    corridorFeatureTypes,
    KmlFeatureGroups,
    KmlUploadService,
    locationFeatureTypes,
    transformLineToCorridor,
    UploadedFeature
} from '../kml-upload.service';

@Component({
    selector: 'kml-upload-dialogue',
    templateUrl: './kml-upload-dialogue.component.html'
})
export class KmlUploadDialogue {
    @Input() features: KmlFeatureGroups[];
    @Input() name: string;
    /** Any feature types that should not be available for selection */
    @Input() suppressFeatureTypes: string[];

    @Output()
    selectedFeatures = new EventEmitter<KmlFeatureGroups[]>();

    areas: KmlFeatureGroups;
    markers: KmlFeatureGroups;
    lines: KmlFeatureGroups;
    corridors: KmlFeatureGroups;

    areaFeatureTypes: NameValue[];
    markerFeatureTypes: NameValue[];
    lineFeatureTypes: NameValue[];
    corridorFeatureTypes: NameValue[];

    locationFeatureTypes = locationFeatureTypes;

    private ngUnsubscribe$ = new Subject<void>();

    private imported = false;

    constructor(
        private kmlUploadService: KmlUploadService,
        private commonDialoguesService: CommonDialoguesService,
        private modal: BsModalRef,
        modalOptions: ModalOptions,
        private angulartics2: Angulartics2,
        private logging: FlyFreelyLoggingService
    ) {
        modalOptions.closeInterceptor = () => {
            if (this.imported) {
                return Promise.resolve();
            } else {
                return this.commonDialoguesService.showConfirmationDialogue(
                    'Unsaved Changes',
                    `You have not completed the import, are you sure you want to cancel?`,
                    'Cancel',
                    () => Promise.resolve()
                );
            }
        };
    }

    ngOnInit() {
        this.setupFeatures();
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    findCategoryName(category: any) {
        return this.kmlUploadService.findFeatureType(category);
    }

    formatFeatureType(type: LocationDetailsDto.Type) {
        return locationFeatureTypes.find(t => t.value === type).name;
    }

    setupFeatures() {
        this.areas = this.features.find(f => f.featureType === 'Areas');
        this.markers = this.features.find(f => f.featureType === 'Markers');
        this.lines = this.features.find(f => f.featureType === 'Lines');
        this.corridors = this.features.find(f => f.featureType === 'Corridors');
        this.areaFeatureTypes = this.findSuitableTypes('Polygon');
        this.markerFeatureTypes = this.findSuitableTypes('Point');
        // Lines can also be imported as corridors
        this.lineFeatureTypes = this.findSuitableTypes('LineString').concat(
            this.findSuitableTypes('GeometryCollection')
        );
        this.corridorFeatureTypes =
            this.findSuitableTypes('GeometryCollection');

        this.normaliseFeatureTypes(this.areas, this.areaFeatureTypes);
        this.normaliseFeatureTypes(this.markers, this.markerFeatureTypes);
        this.normaliseFeatureTypes(this.lines, this.lineFeatureTypes);
        this.normaliseFeatureTypes(this.corridors, this.corridorFeatureTypes);

        // Every area feature gets a one-time buffer input, defaulting to no buffer
        this.areas?.features.forEach(f => {
            f.properties = { ...f.properties, bufferMeters: 0 };
        });

        // Corridor typed lines are buffered on import, so they always need an offset
        this.lines?.features.forEach(f => {
            if (
                this.isCorridorType(f.featureType) &&
                f.properties?.lineOffsetMeters == null
            ) {
                f.properties = { ...f.properties, lineOffsetMeters: 50 };
            }
        });
    }

    /**
     * Normalise features whose type is not available for selection to the first available type
     */
    private normaliseFeatureTypes(
        group: KmlFeatureGroups,
        options: NameValue[]
    ) {
        if (group == null || options == null || options.length === 0) {
            return;
        }
        group.features.forEach(f => {
            if (!options.some(t => t.value === f.featureType)) {
                f.featureType = options[0].value as LocationFeatureDto.Type;
            }
        });
    }

    isCorridorType(type: LocationFeatureDto.Type) {
        return corridorFeatureTypes.includes(type);
    }

    get hasCorridorLines(): boolean {
        return (
            this.lines?.features.some(f => this.isCorridorType(f.featureType)) ??
            false
        );
    }

    onLineTypeChange(feature: UploadedFeature) {
        if (this.isCorridorType(feature.featureType)) {
            feature.properties = {
                ...feature.properties,
                lineOffsetMeters: feature.properties?.lineOffsetMeters ?? 50
            };
        } else if (feature.properties?.lineOffsetMeters != null) {
            const { lineOffsetMeters, ...properties } = feature.properties;
            feature.properties = properties;
        }
    }

    onLineOffsetChange(feature: UploadedFeature) {
        if (feature.properties.lineOffsetMeters < 1) {
            feature.properties.lineOffsetMeters = 1;
        }
    }

    findSuitableTypes(
        type:
            | 'Point'
            | 'MultiPoint'
            | 'LineString'
            | 'MultiLineString'
            | 'Polygon'
            | 'MultiPolygon'
            | 'GeometryCollection'
    ) {
        const availableFeatureTypes = locationFeatureTypes.filter(
            t =>
                this.suppressFeatureTypes == null ||
                !this.suppressFeatureTypes.includes(t.value)
        );
        switch (type) {
            case 'Point':
                return availableFeatureTypes.filter(
                    t =>
                        t.value === LocationFeatureDto.Type.RP ||
                        t.value === LocationFeatureDto.Type.OBSERVER ||
                        t.value === LocationFeatureDto.Type.TAKEOFF ||
                        t.value === LocationFeatureDto.Type.LANDING ||
                        t.value === LocationFeatureDto.Type.TAKEOFF_LANDING ||
                        t.value === LocationFeatureDto.Type.POINT_OF_INTEREST
                );
            case 'Polygon':
                return availableFeatureTypes.filter(
                    t =>
                        t.value === LocationFeatureDto.Type.FLIGHT_AREA ||
                        t.value === LocationFeatureDto.Type.DANGER ||
                        t.value === LocationFeatureDto.Type.NO_FLY ||
                        t.value === LocationFeatureDto.Type.AREA_OF_INTEREST
                );
            case 'LineString':
                return availableFeatureTypes.filter(
                    t =>
                        t.value === LocationFeatureDto.Type.FLIGHT_PATH_LINE ||
                        t.value === LocationFeatureDto.Type.GENERAL_LINE
                );
            case 'GeometryCollection':
                // Only corridors are kept as an intact GeometryCollection.
                return availableFeatureTypes.filter(
                    t =>
                        t.value ===
                            LocationFeatureDto.Type.OFFSET_FLIGHT_AREA ||
                        t.value ===
                            LocationFeatureDto.Type.OFFSET_DANGER_AREA ||
                        t.value === LocationFeatureDto.Type.OFFSET_NO_FLY_AREA
                );
        }
    }

    close() {
        this.modal.hide();
    }

    import() {
        this.transformCorridorLines();
        this.transformBufferedAreas();
        const combined: KmlFeatureGroups[] = [];
        if (this.areas != null) {
            combined.push(this.areas);
        }
        if (this.markers != null) {
            combined.push(this.markers);
        }
        if (this.lines != null) {
            combined.push(this.lines);
        }
        if (this.corridors != null) {
            combined.push(this.corridors);
        }
        this.selectedFeatures.next(combined);

        this.angulartics2.eventTrack.next({
            action: 'complex-kml-import',
            properties: {
                category: `kml-upload`,
                areas_count: this.areas?.features?.length ?? 0,
                points_count: this.markers?.features?.length ?? 0,
                lines_count: this.lines?.features?.length ?? 0,
                corridors_count: this.corridors?.features?.length ?? 0
            }
        });
        this.imported = true;
        this.modal.hide();
    }

    /**
     * Convert any line features that have a corridor type selected into
     * corridor features, moving them into the corridors group.
     */
    private transformCorridorLines() {
        if (this.lines == null) {
            return;
        }
        const corridorLines = this.lines.features.filter(f =>
            this.isCorridorType(f.featureType)
        );
        if (corridorLines.length === 0) {
            return;
        }
        const transformed = corridorLines
            .map(f => {
                const corridor = transformLineToCorridor(f);
                if (corridor == null) {
                    this.logging.warn(
                        `Unable to convert "${f.name}" to a corridor, skipping it`
                    );
                }
                return corridor;
            })
            .filter(f => f != null);
        if (transformed.length > 0) {
            if (this.corridors == null) {
                this.corridors = {
                    type: 'GeometryCollection',
                    featureType: 'Corridors',
                    features: []
                };
            }
            this.corridors.features =
                this.corridors.features.concat(transformed);
        }
        const remainingLines = this.lines.features.filter(
            f => !this.isCorridorType(f.featureType)
        );
        this.lines =
            remainingLines.length > 0
                ? { ...this.lines, features: remainingLines }
                : null;
    }

    /**
     * Apply the configured buffer to any area features that have one, then
     * strip the one-time bufferMeters property from every area feature so it
     * never reaches the backend as a dead LocationFeatureDto property.
     */
    private transformBufferedAreas() {
        if (this.areas == null) {
            return;
        }
        this.areas.features = this.areas.features.map(f =>
            this.applyAreaBuffer(f)
        );
    }

    /** Applies the one-time buffer to an area feature and strips the setting from its properties. */
    private applyAreaBuffer(feature: UploadedFeature): UploadedFeature {
        const { bufferMeters, ...properties } = feature.properties;
        // A negative buffer shrinks the area, so only an absent or zero value
        // means "leave this one alone".
        if (bufferMeters == null || bufferMeters === 0) {
            return { ...feature, properties };
        }
        const buffered = bufferAreaFeature(feature, bufferMeters);
        if (buffered == null) {
            this.logging.warn(
                `Unable to buffer "${feature.name}", keeping its original geometry`
            );
            return { ...feature, properties };
        }
        return { ...buffered, properties };
    }
}

export function showKmlUploadDialogue(
    modalService: BsModalService,
    features: KmlFeatureGroups[],
    name: string,
    suppressFeatureTypes?: string[]
) {
    return modalService.show(KmlUploadDialogue, {
        ...MODAL_OPTIONS,
        class: 'modal-task',
        initialState: { features, name, suppressFeatureTypes }
    });
}
